import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAppContext } from "../../app/AppContext";
import type { CatalogFacets, CatalogSnapshot, Locale } from "../../core/types";
import { categoryLabelMap, keywordLabelMap } from "./category-style";

/**
 * Catalog query hooks shared by every template surface (the gallery, the member detail, the
 * signed-out detail).
 *
 * ONE query key per server resource, for the reason billing-hooks.ts records: copies with
 * different key shapes refetch data already in cache. The gallery reads the whole shelf from
 * the catalog snapshot (one GET the browser's HTTP cache revalidates); the detail routes share
 * one key and start from the snapshot's row when the gallery already loaded it, so opening a
 * card from the gallery asks the server nothing (workflows/V5-Template-Method-Review.md L4).
 * The site is host-fixed per session, so the locale alone keys them. Every catalog read is
 * fresh for five minutes: the catalog changes only when content is published.
 */

const CATALOG_STALE_MS = 5 * 60_000;

export function catalogSnapshotQueryKey(locale: Locale) {
  return ["catalog-snapshot", locale] as const;
}

export function catalogTemplateQueryKey(libraryKey: string, slug: string, locale: Locale) {
  return ["catalog-template", libraryKey, slug, locale] as const;
}

export function catalogFacetsQueryKey(locale: Locale) {
  return ["catalog-facets", locale] as const;
}

/** The snapshot already in cache, and when it arrived, for seeding the other catalog keys. */
function cachedSnapshot(queryClient: QueryClient, locale: Locale) {
  const key = catalogSnapshotQueryKey(locale);
  return {
    snapshot: queryClient.getQueryData<CatalogSnapshot>(key),
    updatedAt: queryClient.getQueryState(key)?.dataUpdatedAt,
  };
}

/** Every live template and the vocabulary, in the locale: the gallery's one fetch. */
export function useCatalogSnapshot(locale: Locale) {
  const { api } = useAppContext();
  return useQuery({
    queryKey: catalogSnapshotQueryKey(locale),
    queryFn: () => api.getCatalogSnapshot(locale),
    staleTime: CATALOG_STALE_MS,
  });
}

/** One template's detail, for the member and the signed-out detail alike, seeded from the snapshot. */
export function useCatalogTemplate(libraryKey: string, slug: string, locale: Locale) {
  const { api } = useAppContext();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: catalogTemplateQueryKey(libraryKey, slug, locale),
    queryFn: () => api.getCatalogTemplate(libraryKey, slug, locale),
    enabled: Boolean(libraryKey && slug),
    staleTime: CATALOG_STALE_MS,
    initialData: () => {
      const row = cachedSnapshot(queryClient, locale).snapshot?.templates.find(
        (template) => template.libraryKey === libraryKey && template.slug === slug,
      );
      return row ? { template: row } : undefined;
    },
    initialDataUpdatedAt: () => cachedSnapshot(queryClient, locale).updatedAt,
  });
}

export function useCatalogFacets(locale: Locale): {
  facets: CatalogFacets | undefined;
  categoryLabels: Map<string, string>;
  /** Keyword and capability key → label (cards and the Keywords filter). */
  keywordLabels: Map<string, string>;
  /** The vocabulary's category keys in file order: what `templateColor` positions by. */
  categoryOrder: readonly string[];
} {
  const { api } = useAppContext();
  const queryClient = useQueryClient();
  const facetsQuery = useQuery({
    queryKey: catalogFacetsQueryKey(locale),
    queryFn: () => api.getCatalogFacets(locale),
    staleTime: CATALOG_STALE_MS,
    // The snapshot carries the same libraries and vocabulary (a library's own categories and
    // tags are always empty), so a detail opened from the gallery needs no facets request.
    initialData: (): CatalogFacets | undefined => {
      const { snapshot } = cachedSnapshot(queryClient, locale);
      return snapshot
        ? {
            libraries: snapshot.libraries.map((library) => ({
              ...library,
              categories: [],
              tags: [],
            })),
            vocabulary: snapshot.vocabulary,
          }
        : undefined;
    },
    initialDataUpdatedAt: () => cachedSnapshot(queryClient, locale).updatedAt,
  });
  const facets = facetsQuery.data;
  const categoryLabels = useMemo(() => categoryLabelMap(facets), [facets]);
  const keywordLabels = useMemo(() => keywordLabelMap(facets), [facets]);
  const categoryOrder = useMemo(
    () => (facets?.vocabulary?.categories ?? []).map((term) => term.key),
    [facets],
  );
  return { facets, categoryLabels, keywordLabels, categoryOrder };
}

/** Reusable AGENT YAML for a template version; idle until the version is known. */
export function useTemplateYaml(templateVersionId: string | undefined) {
  const { api } = useAppContext();
  return useQuery({
    queryKey: ["template-yaml", templateVersionId],
    queryFn: () => api.getTemplateYaml(templateVersionId ?? ""),
    enabled: Boolean(templateVersionId),
  });
}
